import React, { useCallback, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import DetailDrawer, { SEVERITY_BADGE, authHeaders, formatDate, itemStatusBadge, itemStatusLabel } from "./MLCOItemDrawer";

/**
 * BM-H634 (26 Sep 2026): high risk assessments on the dashboard, with a
 * drill-down. Vanessa: they stay with the fee earner first, "but it should show
 * in MLCO dashboard metrics with ability to drill down (in same way the card
 * shows all details in the MLCO inbox)". The list reads the queue filtered to
 * items raised by a risk assessment; each row opens the MLCO Inbox's own panel.
 */
function HighRiskAssessments({ counts, userMeta }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/compliance/mlco-queue?source_event=intake_risk_assessment&limit=200`,
        { headers: authHeaders() }
      );
      setItems(r.data?.items || []);
      setError(r.data?.next_cursor ? "Showing the newest 200. Open the MLCO Inbox for the rest." : "");
    } catch (err) {
      setError(errorMessage(err, "Could not load the high risk assessments"));
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const c = counts || {};
  return (
    <div className="rounded-md border border-orange-800/50 bg-orange-950/20 p-3 space-y-2" data-testid="mlco-high-risk">
      <button type="button" onClick={toggle} className="w-full text-left" data-testid="mlco-high-risk-toggle" aria-expanded={open}>
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-orange-200">High risk assessments</span>
          <span className="text-lg font-semibold text-slate-100" data-testid="mlco-high-risk-open">{c.open ?? 0} open</span>
        </div>
        <div className="text-xs text-slate-400" data-testid="mlco-high-risk-breakdown">
          {c.waiting_for_fee_earner ?? 0} waiting for fee earner · {c.waiting_for_mlco ?? 0} waiting for MLCO ·{" "}
          {c.closed ?? 0} closed · {open ? "hide" : "show"}
        </div>
      </button>
      {open && (
        <div className="space-y-1" data-testid="mlco-high-risk-list">
          {error && <div className="text-xs text-amber-300">{error}</div>}
          {items === null && !error && <div className="text-xs text-slate-400">Loading…</div>}
          {items && items.length === 0 && <div className="text-xs text-slate-400">None.</div>}
          {(items || []).map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setSelected(it.id)}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-800/60"
              data-testid={`mlco-high-risk-row-${it.id}`}
            >
              <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] border ${SEVERITY_BADGE[it.severity] || ""}`}>{it.severity}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-slate-100">{it.matter_ref}</span>
                <span className="block text-xs text-slate-400 truncate">
                  {[it.matter?.client_name, it.matter?.matter_description].filter(Boolean).join(" · ")}
                </span>
              </span>
              <span className="text-right">
                <span className={`block px-1.5 py-0.5 rounded text-[10px] border ${itemStatusBadge(it)}`}>{itemStatusLabel(it)}</span>
                <span className="block text-[10px] text-slate-500">{formatDate(it.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {selected && (
        <DetailDrawer
          itemId={selected}
          userMeta={userMeta}
          onClose={() => setSelected(null)}
          onActed={load}
        />
      )}
    </div>
  );
}

/**
 * Brief 14d — Panel A: MLCO Queue Summary.
 *
 * Shows four queue-state count tiles, an SLA breach callout, and a
 * severity heat table. Visible to MLCO and COLP users.
 */
export default function MLCOQueuePanel({ data, loading, error, userMeta }) {
  if (loading) {
    return (
      <section
        data-testid="mlco-queue-panel"
        className="rounded-md border border-slate-700/60 bg-slate-900/50 p-4 space-y-3"
      >
        <div data-testid="mlco-queue-panel-skeleton" className="h-4 w-40 bg-slate-800 rounded animate-pulse" />
        <div className="h-12 w-full bg-slate-800/70 rounded animate-pulse" />
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="mlco-queue-panel"
        className="rounded-md border border-red-800/50 bg-red-950/30 p-4 text-sm text-red-200"
      >
        <span data-testid="mlco-queue-panel-error">Dashboard data unavailable. Try reloading.</span>
      </section>
    );
  }

  const d = data || {};
  const heat = d.severity_heat || {};
  const openCount = d.open_count ?? 0;
  const mlcoReviewing = d.mlco_reviewing_count ?? 0;
  const colpReviewing = d.colp_reviewing_count ?? 0;
  const decidedCount = d.decided_count ?? 0;
  const slaBreach = d.sla_breach_count ?? 0;

  return (
    <section
      data-testid="mlco-queue-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-4"
    >
      <h2 className="text-sm font-semibold text-slate-100">MLCO Queue</h2>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-2" data-testid="mlco-queue-tiles">
        <Tile testId="tile-open" label="Pending" value={openCount} hue={["#6366f1", "#a855f7"]} />
        <Tile testId="tile-mlco-reviewing" label="MLCO Reviewing" value={mlcoReviewing} hue={["#8b5cf6", "#d946ef"]} />
        <Tile testId="tile-colp-reviewing" label="At COLP" value={colpReviewing} hue={["#3b82f6", "#6366f1"]} />
        <Tile testId="tile-decided-this-month" label="Decided this month" value={decidedCount} hue={["#06b6d4", "#3b82f6"]} />
      </div>

      <HighRiskAssessments counts={d.high_risk_assessments} userMeta={userMeta} />

      {slaBreach > 0 && (
        <div
          className="rounded-md border border-amber-700/60 bg-amber-900/30 text-amber-200 text-sm px-3 py-2"
          data-testid="mlco-queue-sla-callout"
        >
          {slaBreach} matter{slaBreach === 1 ? "" : "s"} have exceeded the 5-business-day SLA.{" "}
          <a className="underline" href="/compliance/mlco-inbox" data-testid="mlco-queue-sla-link">
            View
          </a>
        </div>
      )}

      {/* Severity heat as one stacked bar; the legend chips carry the counts. */}
      <div data-testid="mlco-queue-severity-heat">
        <h3 className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Severity</h3>
        <div className="flex h-2 rounded-full overflow-hidden bg-slate-800">
          {HEAT_BANDS.map(({ band, colour }) => (heat[band] > 0 ? (
            <div key={band} style={{ flexGrow: heat[band], background: colour }} />
          ) : null))}
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
          {HEAT_BANDS.map(({ band, colour }) => (
            <span key={band} data-testid={`heat-row-${band}`} className="flex items-center gap-1 text-xs text-slate-300 capitalize">
              <span className="w-2 h-2 rounded-full" style={{ background: colour }} />
              {band} <span className="tabular-nums font-semibold text-slate-100">{heat[band] ?? 0}</span>
            </span>
          ))}
        </div>
      </div>

      <a
        href="/compliance/mlco-inbox"
        className="text-xs text-indigo-400 hover:underline"
        data-testid="mlco-queue-inbox-link"
      >
        View MLCO Inbox →
      </a>
    </section>
  );
}

const HEAT_BANDS = [
  { band: "critical", colour: "#ef4444" },
  { band: "high", colour: "#f97316" },
  { band: "medium", colour: "#fbbf24" },
  { band: "low", colour: "#64748b" },
];

function Tile({ label, value, testId, hue }) {
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-lg border border-white/[0.07] bg-slate-900/40 px-3 py-2"
    >
      <div aria-hidden className="absolute inset-x-0 top-0 h-0.5" style={{ background: `linear-gradient(90deg, ${hue[0]}, ${hue[1]})` }} />
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-xl font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}
